import { Action, PayloadAction } from '@reduxjs/toolkit';
import {
  SubmitContactFormRequestedPayload,
  submitContactFormFailed,
  submitContactFormSucceeded
} from '../../contact/ContactSlice';
import {
  apiContactFormSubmitFailed,
  apiContactFormSubmitRequested,
  apiContactFormSubmitSucceeded
} from '../../api/contact/submitContactFormApi';
import { put, take } from 'typed-redux-saga';
import generateActionId from '../../../generateActionId';

interface ApiActionPayload {
  requestedActionId: number;
}

interface ApiSuccessPayload extends ApiActionPayload {
  result: { success: boolean };
}

interface ApiFailurePayload extends ApiActionPayload {
  error: Error;
}

type ApiSuccessAction = {
  type: typeof apiContactFormSubmitSucceeded.type;
  payload: ApiSuccessPayload;
};

type ApiFailureAction = {
  type: typeof apiContactFormSubmitFailed.type;
  payload: ApiFailurePayload;
};

type ApiResultAction = ApiSuccessAction | ApiFailureAction;

function isApiSuccessAction(action: ApiResultAction): action is ApiSuccessAction {
  return action.type === apiContactFormSubmitSucceeded.type;
}

function isApiAction(action: Action): action is ApiResultAction {
  return (
    (action.type === apiContactFormSubmitSucceeded.type ||
      action.type === apiContactFormSubmitFailed.type) &&
    'payload' in action &&
    typeof action.payload === 'object' &&
    action.payload !== null &&
    'requestedActionId' in action.payload
  );
}

function* submitContactFormSaga(
  action: PayloadAction<SubmitContactFormRequestedPayload>
): Generator {
  const actionId = generateActionId();

  yield* put(
    apiContactFormSubmitRequested({
      actionId,
      formData: action.payload.formData,
      parentActionId: action.payload.actionId,
      honeypotToken: action.payload.honeypotToken
    })
  );

  const resultAction: ApiResultAction = yield* take(
    (action: Action): action is ApiResultAction =>
      isApiAction(action) && action.payload.requestedActionId === actionId
  );

  if (isApiSuccessAction(resultAction) && resultAction.payload.result.success) {
    yield* put(
      submitContactFormSucceeded({
        requestedActionId: action.payload.actionId,
        result: resultAction.payload.result
      })
    );
    return;
  }

  yield* put(
    submitContactFormFailed({
      requestedActionId: action.payload.actionId
    })
  );
}

export default submitContactFormSaga;
